import classNames from "classnames";
import React, { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  ActionsMenu,
  BillingCheckbox,
  BillingMenuItem,
} from "../billing/ui/BillingUi";
import { FileItem, FolderItem } from "./files.types";
import { formatDate, formatSize, refKey, toRef } from "./files.utils";
import { FileTypeIcon, FolderGlyph, Pill, VisibilityPill } from "./ui/FilesUi";

export type FileColumns = {
  client?: boolean;
  location?: boolean;
  visibility?: boolean;
  addedBy?: boolean;
  added?: boolean;
  size?: boolean;
  work?: boolean;
  status?: boolean;
  deleted?: boolean;
};

/** Client page link for a file's client. */
export const clientLink = (
  file: Pick<
    FileItem,
    "isOrganization" | "businessContactId" | "customerId" | "contactId"
  >,
  tab = "files",
) =>
  file.isOrganization
    ? `/organization/${file.businessContactId}/${tab}`
    : `/contact/${file.customerId}/${tab}?contactId=${file.contactId}`;

const TH =
  "whitespace-nowrap px-2.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A91A5]";
const TD = "px-2.5 py-2.5 align-middle";

/** New / Opened / Filed for client uploads (Documents page). */
export const uploadStatus = (file: FileItem) =>
  file.isNew ? (
    <Pill tone={"new"}>New</Pill>
  ) : file.folderId ? (
    <Pill tone={"ok"}>Filed</Pill>
  ) : (
    <Pill tone={"client"}>Opened, not filed</Pill>
  );

/** The file list used by the client Files tab, the work Files tab and the Documents page. */
export const FileTable: FC<{
  files: FileItem[];
  folders?: FolderItem[];
  columns: FileColumns;
  selected: Set<string>;
  onSelect: (keys: Set<string>) => void;
  onOpen: (file: FileItem) => void;
  onOpenFolder?: (folder: FolderItem) => void;
  onLocation?: (file: FileItem) => void;
  menu: (file: FileItem) => BillingMenuItem[];
  folderMenu?: (folder: FolderItem) => BillingMenuItem[];
  folderCount?: (folder: FolderItem) => number;
  draggable?: boolean;
  onDragFiles?: (keys: string[]) => void;
  extra?: (file: FileItem) => ReactNode;
  empty?: ReactNode;
}> = ({
  files,
  folders = [],
  columns,
  selected,
  onSelect,
  onOpen,
  onOpenFolder,
  onLocation,
  menu,
  folderMenu,
  folderCount,
  draggable,
  onDragFiles,
  extra,
  empty,
}) => {
  const keys = files.map((f) => refKey(toRef(f)));
  const allSelected = keys.length > 0 && keys.every((k) => selected.has(k));

  const toggle = (key: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(key);
    else next.delete(key);
    onSelect(next);
  };

  if (files.length === 0 && folders.length === 0) {
    return <>{empty}</>;
  }

  return (
    <div className={"overflow-x-auto"}>
      <table
        className={
          "w-full min-w-[640px] border-collapse text-[13px] text-[#1F2433]"
        }>
        <thead>
          <tr className={"border-b border-[#E6E9EF]"}>
            <th className={classNames(TH, "w-9")}>
              <BillingCheckbox
                size={17}
                label={"Select all files"}
                checked={allSelected}
                disabled={keys.length === 0}
                onChange={(checked) =>
                  onSelect(
                    checked
                      ? new Set([...Array.from(selected), ...keys])
                      : new Set(
                          Array.from(selected).filter((k) => !keys.includes(k)),
                        ),
                  )
                }
              />
            </th>
            <th className={TH}>Name</th>
            {columns.client && <th className={TH}>Client</th>}
            {columns.location && <th className={TH}>Folder</th>}
            {columns.visibility && <th className={TH}>Client can see</th>}
            {columns.addedBy && <th className={TH}>Added by</th>}
            {columns.added && <th className={TH}>Date</th>}
            {columns.deleted && <th className={TH}>Deleted</th>}
            {columns.size && <th className={TH}>Size</th>}
            {columns.work && <th className={TH}>Work item</th>}
            {columns.status && <th className={TH}>Status</th>}
            <th className={classNames(TH, "w-12")} aria-label={"Actions"} />
          </tr>
        </thead>
        <tbody>
          {folders.map((folder) => (
            <tr
              key={`folder-${folder.id}`}
              className={
                "cursor-pointer border-b border-[#EDEFF3] hover:bg-[#FAFAFD]"
              }
              onDoubleClick={() => onOpenFolder?.(folder)}>
              <td className={TD} />
              <td className={TD}>
                <button
                  type={"button"}
                  onClick={() => onOpenFolder?.(folder)}
                  className={
                    "flex items-center gap-2.5 text-left font-semibold"
                  }>
                  <span
                    className={
                      "flex h-8 w-8 items-center justify-center text-[#6B4EFF]"
                    }>
                    <FolderGlyph size={24} />
                  </span>
                  <span>
                    {folder.name}
                    <span
                      className={"block text-xs font-medium text-[#667085]"}>
                      {folderCount
                        ? `${folderCount(folder)} file${
                            folderCount(folder) === 1 ? "" : "s"
                          }`
                        : "Folder"}
                    </span>
                  </span>
                </button>
              </td>
              {columns.client && <td className={TD} />}
              {columns.location && <td className={TD} />}
              {columns.visibility && (
                <td className={TD}>
                  {folder.effective !== "private" ? (
                    <VisibilityPill
                      visibility={folder.effective}
                      compact={true}
                    />
                  ) : (
                    <span className={"text-[#9AA0B0]"}>No</span>
                  )}
                </td>
              )}
              {columns.addedBy && <td className={TD} />}
              {columns.added && <td className={TD} />}
              {columns.deleted && <td className={TD} />}
              {columns.size && <td className={TD} />}
              {columns.work && <td className={TD} />}
              {columns.status && <td className={TD} />}
              <td className={TD}>
                {folderMenu && (
                  <ActionsMenu items={folderMenu(folder)} size={30} />
                )}
              </td>
            </tr>
          ))}
          {files.map((file) => {
            const key = refKey(toRef(file));
            const isSelected = selected.has(key);
            return (
              <tr
                key={key}
                draggable={draggable}
                onDragStart={(event) => {
                  const dragged = isSelected ? Array.from(selected) : [key];
                  event.dataTransfer.setData(
                    "application/x-procharted-files",
                    JSON.stringify(dragged),
                  );
                  event.dataTransfer.effectAllowed = "move";
                  onDragFiles?.(dragged);
                }}
                className={classNames(
                  "border-b border-[#EDEFF3]",
                  isSelected ? "bg-[#F4F2FF]" : "hover:bg-[#FAFAFD]",
                )}>
                <td className={TD}>
                  <BillingCheckbox
                    size={17}
                    label={`Select ${file.name}`}
                    checked={isSelected}
                    onChange={(checked) => toggle(key, checked)}
                  />
                </td>
                <td className={classNames(TD, "max-w-[360px]")}>
                  {/* Table cells ignore max-width, so the button itself is
                      limited: long names end in "…" (full name on hover). */}
                  <button
                    type={"button"}
                    title={file.name}
                    onClick={() => onOpen(file)}
                    className={
                      "flex w-full min-w-0 max-w-[360px] items-center gap-2.5 text-left"
                    }>
                    <FileTypeIcon name={file.name} />
                    <span className={"min-w-0 flex-1"}>
                      <span className={"flex min-w-0 items-center gap-1.5"}>
                        <span
                          className={
                            "min-w-0 truncate font-semibold hover:text-[#5A3EEB] hover:underline"
                          }>
                          {file.name}
                        </span>
                        {file.isNew && !columns.status && (
                          <Pill tone={"new"}>New</Pill>
                        )}
                      </span>
                      {file.note && (
                        <span
                          className={"block truncate text-xs text-[#667085]"}>
                          “{file.note}”
                        </span>
                      )}
                    </span>
                  </button>
                </td>
                {columns.client && (
                  <td className={TD}>
                    {file.customerId ? (
                      <Link
                        to={clientLink(file)}
                        title={file.customerName || undefined}
                        className={
                          "block max-w-[220px] truncate font-semibold text-[#5A3EEB] hover:underline"
                        }>
                        {file.customerName}
                      </Link>
                    ) : (
                      <span className={"text-[#9AA0B0]"}>Firm work</span>
                    )}
                  </td>
                )}
                {columns.location && (
                  <td className={classNames(TD, "max-w-[220px]")}>
                    {file.kind === "work" && !file.folderId ? (
                      <span className={"text-[#667085]"}>Work item files</span>
                    ) : (
                      <button
                        type={"button"}
                        title={file.folderPath || "Top level"}
                        onClick={() => onLocation?.(file)}
                        className={classNames(
                          "max-w-full truncate text-left",
                          onLocation
                            ? "font-semibold text-[#5A3EEB] hover:underline"
                            : "",
                        )}>
                        {file.folderPath ||
                          (file.fromClient ? "To sort" : "Top level")}
                      </button>
                    )}
                  </td>
                )}
                {columns.visibility && (
                  <td className={TD}>
                    {file.fromClient ? (
                      <Pill tone={"client"}>From client</Pill>
                    ) : file.clientCanSee ? (
                      <Pill tone={"shared"}>Shared</Pill>
                    ) : (
                      <span className={"text-[#9AA0B0]"}>No</span>
                    )}
                  </td>
                )}
                {columns.addedBy && (
                  <td className={classNames(TD, "whitespace-nowrap")}>
                    {file.addedByKind === "signature" ? (
                      <Pill tone={"ok"}>E-signature</Pill>
                    ) : (
                      <>
                        {file.addedBy || (
                          <span className={"text-[#9AA0B0]"}>—</span>
                        )}
                        {file.fromClient && (
                          <span className={"text-[#667085]"}> (client)</span>
                        )}
                      </>
                    )}
                  </td>
                )}
                {columns.added && (
                  <td className={classNames(TD, "whitespace-nowrap")}>
                    {formatDate(file.addedAt)}
                  </td>
                )}
                {columns.deleted && (
                  <td className={classNames(TD, "whitespace-nowrap")}>
                    {formatDate(file.deletedAt)}
                    <span className={"block text-xs text-[#667085]"}>
                      {file.deletedBy ? `by ${file.deletedBy} · ` : ""}
                      {file.daysLeft} day{file.daysLeft === 1 ? "" : "s"} left
                    </span>
                  </td>
                )}
                {columns.size && (
                  <td
                    className={classNames(
                      TD,
                      "whitespace-nowrap text-[#4A5168]",
                    )}>
                    {formatSize(file.size)}
                  </td>
                )}
                {columns.work && (
                  <td className={classNames(TD, "max-w-[200px]")}>
                    {file.workId && file.workName ? (
                      <Link
                        to={`/work/${file.workId}`}
                        className={
                          "block truncate font-semibold text-[#5A3EEB] hover:underline"
                        }>
                        {file.workName}
                      </Link>
                    ) : (
                      <span className={"text-[#9AA0B0]"}>—</span>
                    )}
                  </td>
                )}
                {columns.status && <td className={TD}>{uploadStatus(file)}</td>}
                <td className={TD}>
                  <div className={"flex items-center justify-end gap-1.5"}>
                    {extra?.(file)}
                    <ActionsMenu items={menu(file)} size={30} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
